import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTrial } from "../context/TrialContext";
import BIAViewDialog from "../components/BIAViewDialog";
import BIAWizardDialog from "../components/BIAWizardDialog";
import { BiaConfigWarningBanner } from "../components/BiaConfigWarningBanner";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Users,
  Database,
  RefreshCw,
  Trash2,
  UserPlus,
  Edit,
  Eye,
  CheckSquare,
  XSquare,
  Zap,
  FileCheck,
  FileText,
  Download,
  History,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import DependencyGraph from "../components/DependencyGraph";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AIRecommendationsButton from "../components/AIRecommendationsButton";
import { ColumnChooser } from "../components/bia/ColumnChooser";
import {
  BIA_COLS_KEY,
  COLUMN_ORDER,
  DEFAULT_COL_VIS,
  columnHeaderLabel,
  renderBiaCell,
} from "../components/bia/biaColumns";

export default function OperationalBIAPage() {
  const { user, api } = useAuth();
  const { triggerUpgradeWall } = useTrial();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dependency Graph state
  const [graphData, setGraphData] = useState({ nodes: [], edges: [], summary: {} });
  const [graphLoading, setGraphLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // Task 0 — full re-edit of an existing assessment via the wizard (saves through PUT)
  const [editProcess, setEditProcess] = useState(null);
  const openEditDialog = async (processId) => {
    try {
      const res = await api.get(`/bia-engine/processes/${processId}`);
      setEditProcess(res.data);
      setShowCreateDialog(true);
    } catch (e) {
      console.error("openEditDialog failed:", e);
      toast.error("Failed to load assessment for editing");
    }
  };
  // BP-A Build 2 — assign process owner
  const [assignOwnerFor, setAssignOwnerFor] = useState(null);
  const [pmUsers, setPmUsers] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  const [savingOwner, setSavingOwner] = useState(false);
  const openAssignOwner = async (process) => {
    setAssignOwnerFor(process);
    setSelectedOwnerId("");
    try {
      const res = await api.get("/bcm-admin/users");
      const list = (res.data?.users || res.data || []).filter((u) => u.role === "process_manager");
      setPmUsers(list);
    } catch (e) {
      setPmUsers([]);
      toast.error(extractErrorMessage(e, "Failed to load Process Managers"));
    }
  };
  const saveOwner = async () => {
    if (!assignOwnerFor || !selectedOwnerId) return;
    setSavingOwner(true);
    try {
      const res = await api.put(`/bia-engine/processes/${assignOwnerFor.id}/owner`, { user_id: selectedOwnerId });
      const newName = res.data?.process_owner_name;
      setProcesses((prev) => prev.map((p) => (p.id === assignOwnerFor.id
        ? { ...p, process_owner_name: newName, process_owner_id: res.data?.process_owner_id } : p)));
      toast.success(res.data?.message || "Process owner assigned");
      setAssignOwnerFor(null);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to assign owner"));
    } finally {
      setSavingOwner(false);
    }
  };

  const [showTechnicalDialog, setShowTechnicalDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);

  // Complete Assessment (turn an onboarding-seeded draft into a full BIA)
  const [completePrefill, setCompletePrefill] = useState(null);
  const [completingDraftId, setCompletingDraftId] = useState(null);

  // V22.9 — Technical BIA tab state
  // IT Function Manager defaults to "technical" view; everyone else defaults to "business"
  const [biaType, setBiaType] = useState(user?.role === "it_function_manager" ? "technical" : "business");
  const [technicalProcesses, setTechnicalProcesses] = useState([]);
  
  // Application Profiles for dependencies
  const [applicationProfiles, setApplicationProfiles] = useState([]);
  const [applicationProfilesLoading, setApplicationProfilesLoading] = useState(false);

  // C1/C2 — column chooser visibility (persisted under versioned key)
  const [colVis, setColVis] = useState(() => {
    try {
      const saved = localStorage.getItem(BIA_COLS_KEY);
      if (saved) return { ...DEFAULT_COL_VIS, ...JSON.parse(saved) };
    } catch (e) { /* fall through to defaults */ }
    return DEFAULT_COL_VIS;
  });
  useEffect(() => {
    try { localStorage.setItem(BIA_COLS_KEY, JSON.stringify(colVis)); } catch (e) { /* ignore */ }
  }, [colVis]);
  const toggleCol = (key) => setColVis((prev) => ({ ...prev, [key]: !prev[key] }));
  
  // Stakeholder integration state
  const [stakeholders, setStakeholders] = useState([]);
  // E2 — program sign-off status
  const [signoff, setSignoff] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signoffChanges, setSignoffChanges] = useState(null);
  const fetchSignoffChanges = async () => {
    try {
      const res = await api.get("/bia-engine/program-signoff/changes");
      setSignoffChanges(res.data);
    } catch (e) { /* non-blocking */ }
  };
  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "admin" || user?.role === "it_drp_manager";

  const fetchProcesses = useCallback(async (archived = showArchived) => {
    try {
      const response = await api.get(`/bia-engine/processes${archived ? "?include_archived=true" : ""}`);
      setProcesses(response.data.processes || []);
    } catch (error) {
      console.error("Failed to fetch processes:", error);
    }  }, [api, showArchived]);

  // V22.9 — Technical BIAs (IT Function Manager backbone infra)
  const fetchTechnicalProcesses = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/technical-processes");
      setTechnicalProcesses(response.data.processes || []);
    } catch (error) {
      console.error("Failed to fetch technical processes:", error);
    }
  }, [api]);

  const fetchAvailableProcesses = useCallback(async (excludeId = null) => {
    try {
      const url = excludeId 
        ? `/bia-engine/processes/for-dependency?exclude_id=${excludeId}`
        : "/bia-engine/processes/for-dependency";
      const response = await api.get(url);
      setAvailableProcesses(response.data.processes || []);
    } catch (error) {
      console.error("Failed to fetch available processes:", error);
    }
  }, [api]);

  const fetchGaps = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/gaps");
      setGaps(response.data.gaps || []);
    } catch (error) {
      console.error("Failed to fetch gaps:", error);
    }
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/processes/stats/summary");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [api]);

  // E2 — current program sign-off record (or null)
  const fetchSignoff = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/program-signoff");
      setSignoff(response.data || null);
    } catch (error) {
      console.error("Failed to fetch program sign-off:", error);
    }
  }, [api]);

  const handleSignProgram = async () => {
    setSigning(true);
    try {
      const res = await api.post("/bia-engine/program-signoff/sign");
      setSignoff(res.data);
      toast.success(`BIA program signed off (revision ${res.data?.revision}).`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to sign off the BIA program"));
    } finally {
      setSigning(false);
    }
  };

  const fetchDependencyGraph = useCallback(async () => {
    setGraphLoading(true);
    try {
      const response = await api.get("/dependencies/graph/user");
      setGraphData({
        nodes: response.data.nodes || [],
        edges: response.data.edges || [],
        summary: response.data.summary || {},
      });
    } catch (error) {
      console.error("Error fetching dependency graph:", error);
    } finally {
      setGraphLoading(false);
    }
  }, [api]);

  // Fetch stakeholders for Process Owner selection
  const fetchStakeholders = useCallback(async () => {
    try {
      const response = await api.get("/stakeholders/for-bia-selection");
      if (response.data.success) {
        setStakeholders(response.data.data.stakeholders || []);
      }
    } catch (error) {
      console.error("Failed to fetch stakeholders:", error);
      setStakeholders([]);
    }
  }, [api]);

  // Fetch Application Profiles for Business Application Dependencies
  const fetchApplicationProfiles = useCallback(async () => {
    setApplicationProfilesLoading(true);
    try {
      const response = await api.get("/application-profile/completed");
      setApplicationProfiles(response.data.profiles || []);
    } catch (error) {
      console.error("Failed to fetch application profiles:", error);
      setApplicationProfiles([]);
    } finally {
      setApplicationProfilesLoading(false);
    }
  }, [api]);

  const handleDownloadBiaReport = async (processId) => {
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/bia-engine/processes/${processId}/report/pdf`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BIA_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("BIA report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download BIA report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProcesses(), fetchGaps(), fetchStats(), fetchDependencyGraph(), fetchStakeholders(), fetchApplicationProfiles(), fetchTechnicalProcesses(), fetchSignoff()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProcesses, fetchGaps, fetchStats, fetchDependencyGraph, fetchStakeholders, fetchApplicationProfiles, fetchTechnicalProcesses, fetchSignoff]);

  const openCreateDialog = () => {
    fetchAvailableProcesses();
    setShowCreateDialog(true);
  };

  const openViewDialog = async (process) => {
    try {
      const response = await api.get(`/bia-engine/processes/${process.id}`);
      setSelectedProcess(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error("Failed to load process details");
    }
  };

  const refreshViewDialog = async (process) => {
    try {
      const response = await api.get(`/bia-engine/processes/${process.id}`);
      setSelectedProcess(response.data);
    } catch (e) { /* keep dialog open */ }
    await Promise.all([fetchProcesses(), fetchStats()]);
  };

  // Complete Assessment — open the BIA wizard pre-filled with the draft's seed data.
  const openCompleteAssessment = (process) => {
    setCompletePrefill({
      process_name: process.process_name,
      department: process.department,
      description: process.description,
    });
    setCompletingDraftId(process.id);
    setShowCreateDialog(true);
  };

  const handleDelete = async (processId) => {
    if (!window.confirm("Are you sure you want to delete this Process BIA?")) return;
    
    try {
      await api.delete(`/bia-engine/processes/${processId}`);
      toast.success("Process BIA deleted");
      await Promise.all([fetchProcesses(), fetchGaps(), fetchStats(), fetchSignoff()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete Process BIA");
    }
  };

  const handleApproveGap = async (gapId, action) => {
    try {
      await api.post(`/bia-engine/gaps/${gapId}/approve`, { action, notes: "" });
      toast.success(action === "approve" ? "Gap correction approved and applied!" : "Gap rejected");
      await Promise.all([fetchProcesses(), fetchGaps(), fetchStats(), fetchSignoff()]);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to process gap"));
    }
  };

  const getRTOBandColor = (band) => {
    const colors = {
      band_1: "bg-red-100 text-red-700 border-red-200",
      band_2: "bg-sky-100 text-sky-700 border-sky-200",
      band_3: "bg-amber-100 text-amber-700 border-amber-200",
      band_4: "bg-emerald-100 text-emerald-700 border-emerald-200",
    };
    return colors[band] || colors.band_4;
  };

  // C1/C2 — resolve columns visible for the current mode, in fixed order
  const visibleColumns = COLUMN_ORDER.filter((k) => colVis[k]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="operational-bia-loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="operational-bia-page">
      <BiaConfigWarningBanner />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-blue-100 text-blue-700">PLAN Phase</Badge>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">ISO 22301:8.2.2</Badge>
            {/* E2 — program sign-off status chip */}
            {!signoff && (
              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200" data-testid="signoff-chip-none">
                <FileCheck className="w-3 h-3 mr-1" /> Program not signed
              </Badge>
            )}
            {signoff && signoff.needs_resignature && (
              <Popover onOpenChange={(o) => { if (o) fetchSignoffChanges(); }}>
                <PopoverTrigger asChild>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 cursor-pointer" data-testid="signoff-chip-resign">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Re-signature required
                    {signoffChanges?.count ? ` · ${signoffChanges.count} changed since rev ${signoffChanges.revision}` : ""}
                  </Badge>
                </PopoverTrigger>
                <PopoverContent className="w-80" data-testid="signoff-changes-popover">
                  <p className="text-sm font-semibold mb-2">Changed since rev {signoffChanges?.revision ?? signoff.revision}</p>
                  {(!signoffChanges || signoffChanges.count === 0) && (
                    <p className="text-xs text-slate-500">Loading changes…</p>
                  )}
                  <div className="space-y-1 max-h-60 overflow-auto">
                    {(signoffChanges?.changes || []).map((c, i) => (
                      <div key={i} className="text-xs flex justify-between gap-2 border-b border-slate-100 py-1" data-testid={`signoff-change-${i}`}>
                        <span className="font-medium">{c.process_name || c.process_id || c.reason}</span>
                        <span className="text-slate-400">{c.at ? new Date(c.at).toLocaleString() : ""}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {signoff && !signoff.needs_resignature && (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="signoff-chip-signed">
                <FileCheck className="w-3 h-3 mr-1" /> Signed · rev {signoff.revision}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Operational BIA</h1>
          <p className="text-slate-600 mt-1">
            Analyze business processes and calculate RTO/RPO using the 5×4 Impact Matrix
          </p>
        </div>
        <div className="flex gap-2">
          <AIRecommendationsButton 
            module="bia" 
            contextData={{
              total_processes: stats?.total_processes || 0,
              avg_rto_hours: stats?.avg_rto_hours || 0,
              avg_rpo_hours: stats?.avg_rpo_hours || 0,
              open_gaps: stats?.unapproved_gaps || 0
            }}
            api={api}
          />
          <Button onClick={openCreateDialog} className="bg-sky-700 hover:bg-sky-800" data-testid="new-process-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Process BIA
          </Button>
          {/* E2 — Sign off program (BCM Manager and above) */}
          {(user?.role === "bcm_manager" || user?.role === "admin") && (
            <Button
              onClick={handleSignProgram}
              disabled={signing}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="sign-program-btn"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              {signoff && !signoff.needs_resignature ? "Re-sign Program" : "Sign Off Program"}
            </Button>
          )}
          {(user?.role === "it_function_manager" || user?.role === "it_drp_manager" || user?.role === "bcm_manager" || user?.role === "admin") && (
            <Button onClick={() => { fetchAvailableProcesses(); setShowTechnicalDialog(true); }} className="bg-violet-700 hover:bg-violet-800" data-testid="new-technical-bia-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Technical BIA
            </Button>
          )}
        </div>
      </div>

      {/* V22.9 — BIA Type Toggle */}
      {(technicalProcesses.length > 0 || user?.role === "it_function_manager") && (
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1 w-fit" data-testid="bia-type-toggle">
          <button
            type="button"
            onClick={() => setBiaType("business")}
            data-testid="bia-toggle-business"
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${biaType === "business" ? "bg-white text-sky-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            Business Processes ({processes.length})
          </button>
          <button
            type="button"
            onClick={() => setBiaType("technical")}
            data-testid="bia-toggle-technical"
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${biaType === "technical" ? "bg-white text-violet-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            Technical Processes ({technicalProcesses.length})
          </button>
        </div>
      )}

      {/* Stats Cards — Compact Mockup Style */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { label: "Total Processes", val: stats.total_processes, Icon: FileText, color: "text-sky-700" },
            { label: "Avg RTO", val: `${stats.avg_rto_hours}h`, Icon: Clock, color: "text-amber-500" },
            { label: "Avg RPO", val: `${stats.avg_rpo_hours}h`, Icon: Database, color: "text-purple-500" },
            { label: "Open Gaps", val: stats.unapproved_gaps, Icon: AlertTriangle, color: stats.unapproved_gaps > 0 ? "text-red-500" : "text-slate-400", border: stats.unapproved_gaps > 0 ? "border-red-200" : "" },
          ].map((m) => (
            <div key={m.label} className={`bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all ${m.border || ''}`} data-testid={`stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">{m.label}</p>
                  <p className="text-2xl font-extrabold text-slate-800 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{m.val}</p>
                </div>
                <m.Icon className={`w-8 h-8 ${m.color} opacity-60`} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recently Edited — Quick Resume */}
      {processes.length > 0 && (() => {
        const recentProcesses = [...processes]
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 5);

        const timeAgo = (dateStr) => {
          const diff = Date.now() - new Date(dateStr).getTime();
          const mins = Math.floor(diff / 60000);
          if (mins < 1) return "just now";
          if (mins < 60) return `${mins}m ago`;
          const hrs = Math.floor(mins / 60);
          if (hrs < 24) return `${hrs}h ago`;
          const days = Math.floor(hrs / 24);
          if (days < 7) return `${days}d ago`;
          return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        };

        return (
          <div data-testid="recently-edited-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <History className="w-4 h-4" />
                Recently Edited
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {recentProcesses.map((proc) => (
                <button
                  key={proc.id}
                  onClick={() => openViewDialog(proc)}
                  className="group relative text-left p-3 bg-white border border-slate-200 rounded-lg hover:border-sky-300 hover:shadow-md transition-all duration-200"
                  data-testid={`recent-process-${proc.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={`text-[0.625rem] px-1.5 py-0 ${getRTOBandColor(proc.calculated_rto_band)}`}>
                      {proc.calculated_rto_hours}h
                    </Badge>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <p className="font-medium text-sm text-slate-800 leading-tight line-clamp-2 mb-1.5">
                    {proc.process_name}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[0.6875rem] text-slate-400 truncate max-w-[70%]">{proc.department}</span>
                    <span className="text-[0.625rem] text-slate-400 flex-shrink-0">{timeAgo(proc.updated_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Gaps Alert Section */}
      {gaps.filter(g => !g.approved).length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="gaps-alert-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Dependency Gaps Requiring Attention ({gaps.filter(g => !g.approved).length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              {isBCMAdmin
                ? "These gaps require action from the upstream process owner. You are viewing as BCM Manager (read-only)."
                : "These gaps indicate RTO mismatches. As the upstream process owner, you can approve or reject the alignment."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {gaps.filter(g => !g.approved).slice(0, 5).map((gap) => {
                const isAssignedOwner = gap.assigned_to_user_id === user?.id;
                return (
                  <div key={gap.id} className="p-3 bg-white rounded-lg border border-amber-200" data-testid={`gap-card-${gap.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={gap.severity === "high" ? "destructive" : "warning"}>
                            {gap.severity.toUpperCase()}
                          </Badge>
                          <span className="font-medium text-slate-900">{gap.gap_type.replace("_", " ").toUpperCase()}</span>
                        </div>
                        <p className="text-sm text-slate-600">{gap.message}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          <strong>Recommended:</strong> {gap.recommended_action}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          <strong>Assigned to:</strong> {gap.assigned_to_name || gap.assigned_to_email || "Process Owner"}
                          {isAssignedOwner && <span className="ml-1.5 text-sky-600 font-semibold">(You)</span>}
                        </p>
                      </div>
                      {isAssignedOwner ? (
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => handleApproveGap(gap.id, "approve")}
                            data-testid={`approve-gap-${gap.id}`}
                          >
                            <CheckSquare className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleApproveGap(gap.id, "reject")}
                            data-testid={`reject-gap-${gap.id}`}
                          >
                            <XSquare className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      ) : isBCMAdmin ? (
                        <div className="ml-4 px-3 py-1.5 bg-slate-100 rounded-lg text-xs text-slate-500 font-medium" data-testid={`gap-readonly-${gap.id}`}>
                          Pending Owner Review
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Processes List */}
      <Card data-testid="processes-list-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" />
              Process BIA Assessments
            </span>
            <div className="flex items-center gap-3">
              {biaType !== "technical" && (
                <label className="flex items-center gap-2 text-xs font-normal text-slate-500 cursor-pointer" data-testid="show-archived-toggle">
                  <Switch
                    checked={showArchived}
                    onCheckedChange={(v) => { setShowArchived(v); fetchProcesses(v); }}
                    data-testid="show-archived-switch"
                  />
                  Show archived
                </label>
              )}
              <ColumnChooser colVis={colVis} toggleCol={toggleCol} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <AIPowerSearch
              module="bia_processes"
              onResults={(results) => setAiSearchResults(results)}
              onClear={() => setAiSearchResults(null)}
              placeholder='Try: "find critical processes" or "IT department BIAs"...'
            />
          </div>
          {processes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No {biaType === "technical" ? "Technical" : "Process"} BIAs created yet</p>
              <Button variant="outline" onClick={biaType === "technical" ? () => { fetchAvailableProcesses(); setShowTechnicalDialog(true); } : openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create First {biaType === "technical" ? "Technical" : "Process"} BIA
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid={biaType === "technical" ? "technical-bia-table" : "business-bia-table"}>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((key) => (
                      <TableHead key={key} className={key === "process_name" ? "min-w-[180px]" : ""}>{columnHeaderLabel(key, biaType)}</TableHead>
                    ))}
                    <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(aiSearchResults || (biaType === "technical" ? technicalProcesses : processes)).map((process) => (
                    <TableRow key={process.id} data-testid={`process-row-${process.id}`}>
                      {visibleColumns.map((key) => (
                        <TableCell key={key} data-testid={`cell-${key}-${process.id}`}>{renderBiaCell(key, process, { biaType, applicationProfiles, getRTOBandColor })}</TableCell>
                      ))}
                      <TableCell className="text-right sticky right-0 bg-white">
                        <div className="flex justify-end gap-2 items-center">
                          {process.source === "onboarding_enrichment" && process.can_edit && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 border-violet-200 text-violet-700 hover:bg-violet-50 whitespace-nowrap"
                              onClick={() => openCompleteAssessment(process)}
                              title="Open the BIA wizard pre-filled with this draft to finalize it"
                              data-testid={`complete-assessment-${process.id}`}
                            >
                              <Sparkles className="w-3.5 h-3.5 mr-1" /> Complete Assessment
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDownloadBiaReport(process.id)}
                            disabled={downloadingPdf}
                            title="Download PDF Report"
                            data-testid={`download-report-${process.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openViewDialog(process)} data-testid={`view-process-${process.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {process.can_edit ? (
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(process.id)} data-testid={`edit-process-${process.id}`} title="Edit assessment">
                              <Edit className="w-4 h-4" />
                            </Button>
                          ) : (
                            <span
                              className="text-xs text-slate-400 max-w-[180px] leading-tight"
                              title="Only this process's Process Manager or its Department Head can edit this BIA."
                              data-testid={`edit-readonly-note-${process.id}`}
                            >
                              Only this process's Process Manager or its Department Head can edit this BIA.
                            </span>
                          )}
                          {process.can_assign_owner && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 whitespace-nowrap"
                              onClick={() => openAssignOwner(process)}
                              title="Assign process owner"
                              data-testid={`assign-owner-${process.id}`}
                            >
                              <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign owner
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(process.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BP-A Build 2 — Assign process owner dialog */}
      <Dialog open={!!assignOwnerFor} onOpenChange={(o) => !o && setAssignOwnerFor(null)}>
        <DialogContent data-testid="assign-owner-dialog">
          <DialogHeader>
            <DialogTitle>Assign process owner</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium text-slate-700">Process Manager</label>
            <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
              <SelectTrigger data-testid="assign-owner-select">
                <SelectValue placeholder="Select a Process Manager" />
              </SelectTrigger>
              <SelectContent>
                {pmUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`owner-option-${u.id}`}>
                    {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOwnerFor(null)} data-testid="assign-owner-cancel">Cancel</Button>
            <Button onClick={saveOwner} disabled={!selectedOwnerId || savingOwner} data-testid="save-owner-btn">Save owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dependency Graph Section */}
      <div data-testid="dependency-graph-section">
        <DependencyGraph
          nodes={graphData.nodes}
          edges={graphData.edges}
          summary={graphData.summary}
          loading={graphLoading}
          onRefresh={fetchDependencyGraph}
          title="My Process Dependencies"
          height="500px"
        />
      </div>

      {/* Create Process Dialog (Business mode) */}
      <BIAWizardDialog
        open={showCreateDialog}
        onOpenChange={(o) => { setShowCreateDialog(o); if (!o) { setCompletePrefill(null); setCompletingDraftId(null); setEditProcess(null); } }}
        api={api}
        availableProcesses={availableProcesses}
        applicationProfiles={applicationProfiles}
        applicationProfilesLoading={applicationProfilesLoading}
        stakeholders={stakeholders}
        prefill={completePrefill}
        editProcess={editProcess}
        onSuccess={async () => {
          // Completing an onboarding draft → replace the stub with the new full assessment.
          if (completingDraftId) {
            try {
              await api.delete(`/bia-engine/processes/${completingDraftId}`);
            } catch (error) {
              toast.warning("Your BIA was saved. The original draft could not be removed: " + (error?.response?.data?.detail || "unknown error") + " Ask your BCM Manager to remove it.");
            }
            setCompletingDraftId(null);
            setCompletePrefill(null);
          }
          await Promise.all([fetchProcesses(), fetchGaps(), fetchStats(), fetchTechnicalProcesses(), fetchSignoff()]);
        }}
        triggerUpgradeWall={triggerUpgradeWall}
        processCount={processes.length}
        getRTOBandColor={getRTOBandColor}
        mode="business"
      />

      {/* V22.9 — Technical BIA Wizard (IT Function Manager backbone infra) */}
      <BIAWizardDialog
        open={showTechnicalDialog}
        onOpenChange={setShowTechnicalDialog}
        api={api}
        availableProcesses={availableProcesses}
        applicationProfiles={applicationProfiles}
        applicationProfilesLoading={applicationProfilesLoading}
        stakeholders={stakeholders}
        onSuccess={() => Promise.all([fetchTechnicalProcesses(), fetchProcesses(), fetchStats(), fetchSignoff()])}
        triggerUpgradeWall={triggerUpgradeWall}
        processCount={processes.length + technicalProcesses.length}
        getRTOBandColor={getRTOBandColor}
        mode="technical"
        lockedDepartment="Information Technology"
      />

      {/* View Process Dialog */}
      <BIAViewDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        process={selectedProcess}
        getRTOBandColor={getRTOBandColor}
        api={api}
        onRefresh={refreshViewDialog}
      />
    </div>
  );
}

